/* KADO — 角の道
   Each behavior is a custom element, so it re-initializes on its own when the
   theme editor re-renders a section. */

(() => {
  'use strict';

  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hoverQuery = window.matchMedia('(hover: none)');

  /* ---------- Hero showcase ---------- */

  class KadoShowcase extends HTMLElement {
    static INTERVAL = 4200;

    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-goto]')];
      this.kanjiEl = this.querySelector('[data-showcase-kanji]');
      this.labelEl = this.querySelector('[data-showcase-label]');
      this.current = 0;
      this.paused = false;
      if (this.slides.length < 2) return;

      this.dots.forEach((d) => d.addEventListener('click', () => {
        this.show(Number(d.dataset.goto));
        this.start();
      }));
      this.addEventListener('mouseenter', () => this.pause(true));
      this.addEventListener('mouseleave', () => this.pause(false));
      this.addEventListener('focusin', () => this.pause(true));
      this.addEventListener('focusout', () => this.pause(false));

      this.onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', this.onVisibility);

      // Theme editor: selecting a slide block shows that slide.
      this.onBlockSelect = (e) => {
        const i = this.slides.findIndex((s) => s.dataset.blockId === e.detail.blockId);
        if (i === -1) return;
        this.pause(true);
        this.show(i);
      };
      this.onBlockDeselect = () => this.pause(false);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.start();
    }

    disconnectedCallback() {
      this.stop();
      clearTimeout(this.swapTimer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    show(i) {
      this.current = i;
      this.slides.forEach((img, n) => {
        img.classList.toggle('is-active', n === i);
        img.classList.toggle('is-prev', n < i);
        img.classList.toggle('is-next', n > i);
      });
      this.dots.forEach((d, n) => d.setAttribute('aria-pressed', String(n === i)));
      const slide = this.slides[i];
      if (this.labelEl) this.labelEl.textContent = slide.dataset.label || '';
      if (this.kanjiEl) {
        this.kanjiEl.classList.add('is-swapping');
        clearTimeout(this.swapTimer);
        this.swapTimer = setTimeout(() => {
          this.kanjiEl.textContent = slide.dataset.kanji || '';
          this.kanjiEl.classList.remove('is-swapping');
        }, 250);
      }
    }

    pause(on) {
      this.paused = on;
      on ? this.stop() : this.start();
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    start() {
      this.stop();
      if (motionQuery.matches || this.paused || document.hidden) return;
      this.timer = setInterval(() => this.show((this.current + 1) % this.slides.length), KadoShowcase.INTERVAL);
    }
  }

  /* ---------- Product plates: tap / keyboard reveal ---------- */

  class KadoPlate extends HTMLElement {
    connectedCallback() {
      const toggle = () => this.classList.toggle('is-revealed');
      this.addEventListener('click', () => { if (hoverQuery.matches) toggle(); });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
      });
      this.addEventListener('blur', () => this.classList.remove('is-revealed'));
    }
  }

  /* ---------- Background videos: play only while on screen ---------- */

  class KadoVideo extends HTMLElement {
    connectedCallback() {
      const v = this.querySelector('video');
      if (!v) return;
      v.muted = true;
      v.defaultMuted = true;
      v.playsInline = true;

      if (motionQuery.matches) {
        v.removeAttribute('autoplay');
        v.pause();
        return;
      }
      if (!('IntersectionObserver' in window)) {
        v.play().catch(() => {});
        return;
      }
      this.io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) v.play().catch(() => {});
        else v.pause();
      }, { rootMargin: '200px 0px' });
      this.io.observe(v);
    }

    disconnectedCallback() {
      this.io?.disconnect();
    }
  }

  /* ---------- Waitlist: client-side validation before Shopify's form post ---------- */

  class KadoWaitlist extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.errorEl = this.querySelector('[data-form-error]');

      // After Shopify's redirect back, move focus to the confirmation.
      this.querySelector('[data-done-title]')?.focus({ preventScroll: true });

      if (!this.form || !this.querySelector('[data-waitlist-fields]')) return;

      this.required = [...this.form.querySelectorAll('input[required]')];

      this.form.addEventListener('submit', (e) => {
        if (!this.validate()) {
          e.preventDefault();
          return;
        }
        const btn = this.form.querySelector('button[type="submit"]');
        if (btn) {
          btn.disabled = true;
          btn.textContent = btn.dataset.sending || 'Sending…';
        }
      });

      this.form.addEventListener('input', (e) => {
        const el = e.target;
        if (el.getAttribute('aria-invalid') === 'true' && this.isValid(el)) el.setAttribute('aria-invalid', 'false');
      });

      // "Join the waitlist" on a paddle card preselects that paddle.
      this.onPick = (e) => {
        const link = e.target.closest('[data-pick]');
        if (!link) return;
        const radio = this.form.querySelector(`input[data-pick-value="${CSS.escape(link.dataset.pick)}"]`);
        if (radio) radio.checked = true;
      };
      document.addEventListener('click', this.onPick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onPick);
    }

    isValid(el) {
      return el.value.trim() !== '' && el.checkValidity();
    }

    validate() {
      let firstBad = null;
      this.required.forEach((el) => {
        const ok = this.isValid(el);
        el.setAttribute('aria-invalid', String(!ok));
        if (!ok && !firstBad) firstBad = el;
      });
      if (firstBad) {
        this.showError(firstBad.type === 'email' && firstBad.value.trim()
          ? 'Please enter a valid email address.'
          : 'Please add your name and email.');
        firstBad.focus();
        return false;
      }
      this.showError('');
      return true;
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = !msg;
    }
  }

  customElements.define('kado-showcase', KadoShowcase);
  customElements.define('kado-plate', KadoPlate);
  customElements.define('kado-video', KadoVideo);
  customElements.define('kado-waitlist', KadoWaitlist);
})();
